import { saunaArticles, SAUNA_MAIN, SAUNA_QUOTE, type SaunaSeoPage, type SaunaSection } from "../data/sauna-seo";
import { saunaLocationServices } from "../data/sauna-locations";
import { SaunaLocationContent } from "./sauna-location-content";
import { SaunaServiceContent } from "./sauna-service-content";
import { SaunaRange } from "./sauna-range";

const EMBER = "hsl(18 82% 52%)";
const EMBER_SOFT = "hsl(22 90% 66%)";
const NAVY = "hsl(222 47% 11%)";

const SERVICE_CARDS: Record<string, { image: string; alt: string; blurb: string }> = {
  "/sauna/install/home-sauna-installation": { image: "sauna-detail-1.webp", alt: "Timber sauna interior with bench seating", blurb: "A traditional sauna in a suitable spare room, wellness room or refurbishment, specified around the space and your household." },
  "/sauna/install/outdoor-sauna-installation": { image: "sauna-assembly.webp", alt: "Timber sauna cabin being assembled", blurb: "Garden cabins and outdoor buildings, coordinated with the base, delivery route, weather protection and electrical supply." },
  "/sauna/install/commercial-sauna-installation": { image: "sauna-fitting.webp", alt: "Engineer fitting a timber sauna entrance", blurb: "New and replacement facilities for gyms, hotels, spas and wellness operators, planned around usage and maintenance access." },
};

const SECTION_IMAGES = ["sauna-heater.webp", "sauna-detail-3.webp", "sauna-fitting.webp", "sauna-detail-1.webp"];

const isExternal = (href: string) => /^https?:\/\//.test(href);
const join = (base: string, href: string) => (isExternal(href) ? href : `${base.replace(/\/+$/, "")}${href}`);

function Arrow() {
  return (
    <svg aria-hidden="true" viewBox="0 0 16 16" className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M3 8h10M9 4l4 4-4 4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function LinkList({ links, base }: { links: NonNullable<SaunaSection["links"]>; base: string }) {
  return (
    <ul className="mt-6 grid gap-2">
      {links.map((l) => {
        const ext = isExternal(l.href);
        const quote = l.href === SAUNA_QUOTE;
        return (
          <li key={l.href + l.label}>
            <a
              href={join(base, l.href)}
              {...(ext ? { target: "_blank", rel: "noopener noreferrer" } : {})}
              className={`group inline-flex items-center gap-3 rounded-xl px-4 py-3 font-semibold transition-colors ${quote ? "text-white" : "bg-card border border-border hover:border-[hsl(18_82%_52%)] text-foreground"}`}
              style={quote ? { background: EMBER } : undefined}
            >
              <span>{l.label}</span>
              {ext && <span className="text-xs font-normal opacity-70">(external)</span>}
              <span className="transition-transform group-hover:translate-x-1"><Arrow /></span>
            </a>
          </li>
        );
      })}
    </ul>
  );
}

function Paragraphs({ items }: { items: string[] }) {
  return <>{items.map((p, i) => <p key={i} className="text-muted-foreground leading-relaxed text-[1.05rem] mb-4 last:mb-0">{p}</p>)}</>;
}

function Bullets({ items }: { items: string[] }) {
  return (
    <ul className="mt-6 grid gap-3">
      {items.map((b) => (
        <li key={b} className="flex gap-3 leading-relaxed">
          <span aria-hidden className="mt-2.5 w-2 h-2 rounded-full shrink-0" style={{ background: EMBER }} />
          <span>{b}</span>
        </li>
      ))}
    </ul>
  );
}

function StepsSection({ s, n, base }: { s: SaunaSection; n: number; base: string }) {
  const steps = (s.bullets ?? []).map((b) => {
    const m = b.match(/^(\d+)\.\s*(.*?)\s+—\s+(.*)$/);
    return m ? { num: m[1], title: m[2], body: m[3], raw: b } : { num: "", title: "", body: b, raw: b };
  });
  return (
    <section className="py-12 lg:py-16 text-white" style={{ background: NAVY }}>
      <div className="container mx-auto px-4 max-w-7xl">
        <span className="font-mono text-sm" style={{ color: EMBER_SOFT }}>{String(n).padStart(2, "0")}</span>
        <h2 className="font-display font-bold text-3xl md:text-4xl mt-2 mb-4 max-w-2xl">{s.heading}</h2>
        <div className="text-white/70 max-w-2xl [&_p]:text-white/70"><Paragraphs items={s.paragraphs} /></div>
        <ol className="mt-12 grid sm:grid-cols-2 lg:grid-cols-5 gap-px bg-white/10 rounded-2xl overflow-hidden">
          {steps.map((st, i) => (
            <li key={st.raw} className="p-6" style={{ background: NAVY }}>
              <span className="font-mono text-3xl font-bold" style={{ color: i === 0 ? EMBER : "hsl(0 0% 100% / 0.35)" }}>{st.num || i + 1}</span>
              {st.title && <h3 className="font-display font-bold text-lg mt-3 mb-2">{st.title}</h3>}
              <p className="text-white/65 text-sm leading-relaxed">{st.body}</p>
            </li>
          ))}
        </ol>
        {s.links && <LinkList links={s.links} base={base} />}
      </div>
    </section>
  );
}

function ServiceCards({ s, n, base }: { s: SaunaSection; n: number; base: string }) {
  return (
    <section className="py-12 lg:py-16 bg-secondary/50 border-y border-border">
      <div className="container mx-auto px-4 max-w-7xl">
        <div className="grid lg:grid-cols-12 gap-8 mb-12">
          <div className="lg:col-span-5">
            <span className="font-mono text-sm" style={{ color: EMBER }}>{String(n).padStart(2, "0")}</span>
            <h2 className="font-display font-bold text-3xl md:text-4xl mt-2">{s.heading}</h2>
          </div>
          <div className="lg:col-span-7 lg:pt-8"><Paragraphs items={s.paragraphs} /></div>
        </div>
        <ul className="grid md:grid-cols-3 gap-6">
          {(s.links ?? []).map((l, i) => {
            const c = SERVICE_CARDS[l.href];
            return (
              <li key={l.href} className={i === 1 ? "md:translate-y-8" : ""}>
                <a href={join(base, l.href)} className="group block h-full rounded-2xl overflow-hidden bg-card border border-border hover:border-[hsl(18_82%_52%)] transition-colors">
                  {c && <div className="aspect-[4/3] overflow-hidden"><img src={join(base, `/images/${c.image}`)} alt={c.alt} loading="lazy" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]" /></div>}
                  <div className="p-6">
                    <span className="font-mono text-xs text-muted-foreground">0{i + 1}</span>
                    <h3 className="font-display font-bold text-xl mt-1 mb-2">{l.label}</h3>
                    {c && <p className="text-muted-foreground text-sm leading-relaxed mb-4">{c.blurb}</p>}
                    <span className="inline-flex items-center gap-2 font-semibold" style={{ color: EMBER }}>View service <Arrow /></span>
                  </div>
                </a>
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
}

function StandardSection({ s, n, base, imageIdx }: { s: SaunaSection; n: number; base: string; imageIdx: number | null }) {
  const img = imageIdx !== null ? SECTION_IMAGES[imageIdx % SECTION_IMAGES.length] : null;
  return (
    <section className="py-10 lg:py-14 border-b border-border last:border-b-0">
      <div className="container mx-auto px-4 max-w-7xl grid lg:grid-cols-12 gap-8 lg:gap-12">
        <div className="lg:col-span-4">
          <span className="font-mono text-sm" style={{ color: EMBER }}>{String(n).padStart(2, "0")}</span>
          <h2 className="font-display font-bold text-2xl md:text-3xl mt-2 leading-tight">{s.heading}</h2>
          {img && <img src={join(base, `/images/${img}`)} alt="" aria-hidden="true" loading="lazy" className="hidden lg:block mt-8 rounded-2xl w-full aspect-[4/5] object-cover" />}
        </div>
        <div className="lg:col-span-8 max-w-3xl">
          {s.paragraphs.length > 0 && <Paragraphs items={s.paragraphs} />}
          {s.bullets && <Bullets items={s.bullets} />}
          {s.links && <LinkList links={s.links} base={base} />}
        </div>
      </div>
    </section>
  );
}

export function SaunaSeoContent({ page, basePath = "/" }: { page: SaunaSeoPage; basePath?: string }) {
  const localService = saunaLocationServices.find(s => s.slug === page.serviceSlug);
  const quoteHref = localService ? `${SAUNA_QUOTE}&service=${encodeURIComponent(`Sauna — ${localService.title}`)}` : SAUNA_QUOTE;
  const base = basePath;
  const crumbs: { href?: string; label: string }[] = [{ href: "/", label: "Home" }];
  if (page.path !== SAUNA_MAIN) crumbs.push({ href: SAUNA_MAIN, label: "Sauna installation" });
  if (page.kind === "article") crumbs.push({ href: "/sauna/guides", label: "Sauna guides" });
  crumbs.push({ label: page.h1 });
  const guides = saunaArticles.filter((a) => a.status === "published" && a.path !== page.path);
  let imgCounter = 0;

  return (
    <article className="bg-background">
      <header className="relative overflow-hidden text-white" style={{ background: `linear-gradient(160deg, ${NAVY} 0%, hsl(218 52% 16%) 60%, hsl(14 45% 18%) 130%)` }}>
        <div aria-hidden className="absolute inset-0 opacity-[0.06]" style={{ backgroundImage: "repeating-linear-gradient(90deg, #fff 0 1px, transparent 1px 64px)" }} />
        <div className="container relative mx-auto px-4 max-w-7xl pt-8 pb-16 lg:pb-20">
          <nav aria-label="Breadcrumb" className="mb-10">
            <ol className="flex flex-wrap items-center gap-2 text-sm text-white/60">
              {crumbs.map((c, i) => (
                <li key={c.label} className="flex items-center gap-2">
                  {i > 0 && <span aria-hidden>/</span>}
                  {c.href ? <a href={join(base, c.href)} className="hover:text-white underline-offset-4 hover:underline">{c.label}</a> : <span aria-current="page" className="text-white/90">{c.label}</span>}
                </li>
              ))}
            </ol>
          </nav>
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] mb-5" style={{ color: EMBER_SOFT }}>
                {page.kind === "article" ? "Sauna guide" : page.kind === "index" ? "Guides and advice" : "Sauna services"}
              </p>
              <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mb-6">{page.h1}</h1>
              <p className="text-lg md:text-xl text-white/75 leading-relaxed max-w-2xl mb-8">{page.intro}</p>
              {page.kind === "article" && (
                <p className="text-sm text-white/60 mb-8 border-l-2 pl-4" style={{ borderColor: EMBER }}>
                  By <span className="font-semibold text-white">VertexAir Climate Systems</span> — sauna installation team
                </p>
              )}
              <div className="flex flex-col sm:flex-row gap-3">
                <a href={join(base, quoteHref)} className="inline-flex items-center justify-center gap-2 rounded-full h-14 px-8 font-bold text-white transition-transform motion-safe:hover:-translate-y-0.5" style={{ background: EMBER }}>
                  Request a sauna quote <Arrow />
                </a>
                <a href="tel:02080870906" className="inline-flex items-center justify-center rounded-full h-14 px-8 font-semibold border border-white/25 hover:bg-white/10 transition-colors">020 8087 0906</a>
              </div>
            </div>
            <figure className="lg:col-span-5">
              <img src={join(base, `/images/${page.image}`)} alt={page.imageAlt} width={768} height={1024} className="w-full aspect-[4/5] max-h-[560px] object-cover rounded-2xl ring-1 ring-white/10" />
            </figure>
          </div>
        </div>
      </header>

      {page.kind === "service" && <SaunaRange />}
      {page.locationKey ? <SaunaLocationContent sections={page.sections} base={base} image={page.image} /> : page.kind === "service" ? <SaunaServiceContent sections={page.sections} base={base} image={page.image} /> : page.sections.map((s, i) => {
        const n = i + 1;
        if (s.links && s.links.length > 0 && s.links.every((l) => SERVICE_CARDS[l.href])) return <ServiceCards key={s.heading} s={s} n={n} base={base} />;
        if (s.bullets && s.bullets.length >= 3 && s.bullets.every((b) => /^\d+\./.test(b))) return <StepsSection key={s.heading} s={s} n={n} base={base} />;
        const useImg = page.kind !== "article" && s.paragraphs.join(" ").length > 500 ? imgCounter++ : null;
        return <StandardSection key={s.heading} s={s} n={n} base={base} imageIdx={useImg} />;
      })}

      {page.locationKey && (
        <section id="sauna-inspiration" className="py-12 bg-secondary/40 scroll-mt-28" aria-labelledby="sauna-inspiration-heading">
          <div className="container mx-auto px-4 max-w-7xl">
            <h2 id="sauna-inspiration-heading" className="font-display text-3xl font-bold mb-3">Sauna inspiration for your space</h2>
            <p className="text-muted-foreground mb-7 max-w-2xl">Explore glass-front cabins, timber seating and warm lighting. Share your preferred style and room dimensions so we can discuss a suitable design.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                "Glass-front sauna with tiered timber seating",
                "Sauna interior with timber bench and stone heater",
                "Glass-front sauna beside a bathroom shower",
                "Warmly lit sauna in a wellness room",
              ].map((caption, index) => (
                <figure key={caption} className="rounded-2xl overflow-hidden bg-card border border-border">
                  <img src={join(base, `/images/home-sauna-slide-${index + 1}.webp`)} alt={caption} width={768} height={1024} loading="lazy" decoding="async" className="w-full aspect-[4/5] object-cover" />
                  <figcaption className="p-4 text-sm font-medium">{caption}</figcaption>
                </figure>
              ))}
            </div>
          </div>
        </section>
      )}

      {(page.kind === "index" || page.kind === "article") && guides.length > 0 && (
        <section className="py-10 lg:py-14 bg-secondary/50 border-y border-border">
          <div className="container mx-auto px-4 max-w-7xl">
            <h2 className="font-display font-bold text-2xl md:text-3xl mb-8">{page.kind === "index" ? "Published guides" : "More sauna guides"}</h2>
            <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {guides.map((g) => (
                <li key={g.path}>
                  <a href={join(base, g.path)} className="group flex flex-col h-full rounded-2xl bg-card border border-border p-6 hover:border-[hsl(18_82%_52%)] transition-colors">
                    <h3 className="font-display font-bold text-lg mb-2">{g.h1}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed flex-1">{g.description}</p>
                    <span className="mt-4 inline-flex items-center gap-2 font-semibold text-sm" style={{ color: EMBER }}>Read guide <Arrow /></span>
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {page.faqs && page.faqs.length > 0 && (
        <section className="py-10 lg:py-14">
          <div className="container mx-auto px-4 max-w-4xl">
            <h2 className="font-display font-bold text-3xl md:text-4xl mb-10">Sauna installation questions</h2>
            <div className="divide-y divide-border border-y border-border">
              {page.faqs.map((f) => (
                <details key={f.question} className="group py-5">
                  <summary className="flex cursor-pointer list-none items-start justify-between gap-6 font-display font-semibold text-lg">
                    <h3>{f.question}</h3>
                    <span aria-hidden className="mt-1 text-2xl leading-none transition-transform group-open:rotate-45" style={{ color: EMBER }}>+</span>
                  </summary>
                  <p className="mt-3 text-muted-foreground leading-relaxed max-w-3xl">{f.answer}</p>
                </details>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="text-white" style={{ background: NAVY }}>
        <div className="container mx-auto px-4 max-w-7xl py-14 flex flex-col md:flex-row md:items-center justify-between gap-8">
          <div>
            <h2 className="font-display font-bold text-3xl mb-2">Tell us about your sauna project.</h2>
            <p className="text-white/70">Share your postcode, dimensions and photographs for a tailored quotation.</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <a href={join(base, quoteHref)} className="inline-flex items-center justify-center gap-2 rounded-full h-14 px-8 font-bold text-white" style={{ background: EMBER }}>Request a quote <Arrow /></a>
            <a href="tel:02080870906" className="inline-flex items-center justify-center rounded-full h-14 px-6 font-semibold border border-white/25 hover:bg-white/10">020 8087 0906</a>
            <a href="mailto:info@vertexclimate.co.uk" className="inline-flex items-center justify-center rounded-full h-14 px-6 font-semibold border border-white/25 hover:bg-white/10">info@vertexclimate.co.uk</a>
          </div>
        </div>
      </section>
    </article>
  );
}
